// 文件验证工具函数

import { AUDIO_UPLOAD_MAX_BYTES } from './uploadLimits';

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
  warning?: string;
}

export interface FileValidationOptions {
  maxSize: number; // 字节
  allowedTypes: string[];
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  requireSquare?: boolean; // 要求正方形
}

// 文件大小格式化
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// 验证文件类型
export function validateFileType(file: File, allowedTypes: string[]): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase();
  return extension ? allowedTypes.includes(extension) : false;
}

// 验证文件大小
export function validateFileSize(file: File, maxSize: number): boolean {
  return file.size <= maxSize;
}

// 验证图片尺寸
export function validateImageDimensions(
  file: File, 
  minWidth?: number, 
  maxWidth?: number, 
  minHeight?: number, 
  maxHeight?: number,
  requireSquare?: boolean
): Promise<{ isValid: boolean; width?: number; height?: number }> {
  return new Promise((resolve) => {
    if (!minWidth && !maxWidth && !minHeight && !maxHeight && !requireSquare) {
      resolve({ isValid: true });
      return;
    }

    const img = new Image();
    img.onload = () => {
      let isValid = true;
      
      // 检查宽度
      if (minWidth && img.width < minWidth) isValid = false;
      if (maxWidth && img.width > maxWidth) isValid = false;
      
      // 检查高度
      if (minHeight && img.height < minHeight) isValid = false;
      if (maxHeight && img.height > maxHeight) isValid = false;
      
      // 检查正方形
      if (requireSquare && img.width !== img.height) isValid = false;
      
      resolve({ 
        isValid, 
        width: img.width, 
        height: img.height 
      });
    };
    img.onerror = () => resolve({ isValid: false });
    
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

// 综合文件验证
export async function validateFile(
  file: File, 
  options: FileValidationOptions
): Promise<FileValidationResult> {
  // 验证文件类型
  if (!validateFileType(file, options.allowedTypes)) {
    return {
      isValid: false,
      error: `不支持的文件类型。支持的类型: ${options.allowedTypes.join(', ')}`
    };
  }

  // 验证文件大小
  if (!validateFileSize(file, options.maxSize)) {
    return {
      isValid: false,
      error: `文件大小超过限制。最大允许: ${formatFileSize(options.maxSize)}，当前大小: ${formatFileSize(file.size)}`
    };
  }

  // 验证图片尺寸（如果是图片文件，但跳过 SVG 文件，因为 SVG 是矢量图，尺寸验证不适用）
  const isSvgFile = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
  
  if (!isSvgFile && file.type.startsWith('image/') && (options.minWidth || options.maxWidth || options.minHeight || options.maxHeight || options.requireSquare)) {
    const dimensionResult = await validateImageDimensions(
      file, 
      options.minWidth, 
      options.maxWidth, 
      options.minHeight, 
      options.maxHeight,
      options.requireSquare
    );
    
    if (!dimensionResult.isValid) {
      const dimensions = [];
      if (options.requireSquare) dimensions.push('必须是正方形');
      if (options.minWidth) dimensions.push(`最小宽度: ${options.minWidth}px`);
      if (options.maxWidth) dimensions.push(`最大宽度: ${options.maxWidth}px`);
      if (options.minHeight) dimensions.push(`最小高度: ${options.minHeight}px`);
      if (options.maxHeight) dimensions.push(`最大高度: ${options.maxHeight}px`);
      
      let errorMessage = `图片尺寸不符合要求。${dimensions.join(', ')}`;
      
      // 显示当前尺寸信息
      if (dimensionResult.width && dimensionResult.height) {
        errorMessage += ` (当前尺寸: ${dimensionResult.width}x${dimensionResult.height}px)`;
      }
      
      // 如果超过最大尺寸，给出建议
      if (options.maxWidth && dimensionResult.width && dimensionResult.width > options.maxWidth) {
        errorMessage += `。建议：请将图片调整为 ${options.maxWidth}x${options.maxWidth}px 或更小`;
      } else if (options.maxHeight && dimensionResult.height && dimensionResult.height > options.maxHeight) {
        errorMessage += `。建议：请将图片调整为 ${options.maxHeight}x${options.maxHeight}px 或更小`;
      }
      
      return {
        isValid: false,
        error: errorMessage
      };
    }
  }

  // 检查文件大小是否接近限制（给出警告）
  const warningThreshold = options.maxSize * 0.8; // 80%时给出警告
  if (file.size > warningThreshold) {
    return {
      isValid: true,
      warning: `文件较大 (${formatFileSize(file.size)})，建议压缩后上传`
    };
  }

  return { isValid: true };
}

// 预设的验证配置
export const FILE_VALIDATION_CONFIGS = {
  avatar: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['jpg', 'jpeg', 'png', 'webp'],
    requireSquare: true,
    minWidth: 1080,
    minHeight: 1080,
    maxWidth: 4000,
    maxHeight: 4000
  },
  gallery: {
    maxSize: 10 * 1024 * 1024, // 10MB (艺人相册单张图片)
    allowedTypes: ['jpg', 'jpeg', 'png', 'webp']
  },
  albumCover: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['jpg', 'jpeg', 'png', 'webp'],
    requireSquare: true,
    minWidth: 1600,
    minHeight: 1600,
    maxWidth: 5000,
    maxHeight: 5000
  },
  idCard: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['jpg', 'jpeg', 'png', 'webp']
  },
  audio: {
    maxSize: AUDIO_UPLOAD_MAX_BYTES,
    allowedTypes: ['mp3', 'wav', 'flac', 'aac', 'm4a']
  },
  document: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['pdf', 'doc', 'docx', 'txt']
  }
};

const AUDIO_UPLOAD_MIME_TYPES = [
  'audio/mp3',
  'audio/mpeg',
  'audio/wav',
  'audio/flac',
  'audio/aac',
  'audio/mp4'
] as const;

const AUDIO_UPLOAD_EXTENSIONS = ['.mp3', '.wav', '.flac', '.aac', '.m4a'] as const;

export function isAudioUploadFormatSupported(file: File): boolean {
  if (AUDIO_UPLOAD_MIME_TYPES.includes(file.type as (typeof AUDIO_UPLOAD_MIME_TYPES)[number])) {
    return true;
  }
  const fileName = file.name.toLowerCase();
  return AUDIO_UPLOAD_EXTENSIONS.some((ext) => fileName.endsWith(ext));
}

export async function validateAudioForUpload(file: File): Promise<FileValidationResult> {
  if (!isAudioUploadFormatSupported(file)) {
    return {
      isValid: false,
      error: '不支持的音频格式！请上传 MP3、WAV、FLAC 或 AAC 格式的文件。'
    };
  }
  return validateFile(file, FILE_VALIDATION_CONFIGS.audio);
} 